import { useIntl, type UserRequirementDocumentCategory, useSetPageTitle } from '@capsicum/common';
import { Snackbar, Stepper, useStepperConfirmationModal } from '@capsicum/common-web';
import { type SourceOfIncomeProps, useSourceOfIncome } from '@capsicum/features/regulations/shared';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { useIdentityVerificationModals } from '../../hooks/useIdentityVerificationModals';
import { CredentialGuardLoader } from '../CredentialGuardLoader';

import { CategoryStep } from './CategoryStep';
import { SelectionStep } from './SelectionStep';
import { SubmissionStep } from './SubmissionStep';

export const SourceOfIncome = ({
	isForcedRequirement,
	isUniqueRequirement,
	onDismiss,
	requirement,
	status
}: SourceOfIncomeProps) => {
	const {
		areDocumentsRequested,
		availableCategories,
		categoryOptions,
		deleteAllDocuments,
		handleCheckbox,
		handleSubmit,
		isFirstButtonDisabled,
		isMaxFilesAmountExceeded,
		isSubmitting,
		isUnderReview,
		maxFilesAmount,
		navigateToCategory,
		selectedCategories,
		setStepIndex,
		showLoader,
		stepCounter,
		stepperRef,
		storeCategoryRef,
		submissionList
	} = useSourceOfIncome({ isUniqueRequirement, requirement, status });
	const { showUnderReviewModal } = useIdentityVerificationModals();
	const { $t } = useIntl();
	const { openExitConfirmationModal } = useStepperConfirmationModal();

	useSetPageTitle($t('kyc.level4Text'));

	useEffect(() => {
		if (isUnderReview) showUnderReviewModal(onDismiss);
	}, [isUnderReview]);

	const onConfirmDeletion = useCallback(
		(categoryId: string) => {
			const component = (
				<Snackbar
					description={$t('sourceOfIncome.modalDocuments.toastMessage')}
					testID="source-of-income-documents-deleted-toast"
					variant="success"
				/>
			);

			deleteAllDocuments(categoryId, component);
		},
		[deleteAllDocuments]
	);

	const showConfirmationModal = useCallback(
		(categoryId: string) => {
			openExitConfirmationModal({
				buttonConfirm: $t('sourceOfIncome.modalDocuments.confirmButton'),
				description: $t('sourceOfIncome.modalDocuments.description'),
				onConfirmation: () => onConfirmDeletion(categoryId),
				testID: 'source-of-income-delete-all-documents',
				title: $t('sourceOfIncome.modalDocuments.title')
			});
		},
		[onConfirmDeletion, openExitConfirmationModal]
	);

	const steps = useMemo(
		() => [
			...(areDocumentsRequested
				? []
				: [() => <SelectionStep categoryOptions={categoryOptions} handleCheckbox={handleCheckbox} />]),
			...selectedCategories.map(categoryId => {
				const category = availableCategories.find(
					category => category.id === categoryId
				) as UserRequirementDocumentCategory;

				const CategoryStepComponent = () => (
					<CategoryStep
						category={category}
						key={categoryId}
						ref={ref => storeCategoryRef(categoryId, ref)}
						stepCounter={stepCounter}
					/>
				);

				CategoryStepComponent.displayName = `CategoryStepComponent-${categoryId}`;

				return CategoryStepComponent;
			}),
			() => (
				<SubmissionStep
					isMaxFilesAmountExceeded={isMaxFilesAmountExceeded}
					maxFilesAmount={maxFilesAmount}
					navigateToCategory={navigateToCategory}
					submissionList={submissionList}
					onDeleteAllDocuments={showConfirmationModal}
				/>
			)
		],
		[
			areDocumentsRequested,
			availableCategories,
			categoryOptions,
			handleCheckbox,
			navigateToCategory,
			selectedCategories,
			stepCounter,
			submissionList,
			isMaxFilesAmountExceeded
		]
	);

	if (showLoader) return <CredentialGuardLoader />;

	return (
		<S.StepperWrapper>
			<Stepper
				customBackNavigation={onDismiss}
				disabledFirstButton={isFirstButtonDisabled}
				isLoading={isSubmitting}
				onlyButtonsNavigation={isForcedRequirement}
				ref={stepperRef}
				steps={steps}
				testID="source-of-income-stepper"
				onStepIndexChange={setStepIndex}
				onSubmitSteps={handleSubmit}
			/>
		</S.StepperWrapper>
	);
};

namespace S {
	export const StepperWrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		margin: ${({ theme }) => `${theme.spacings.medium} 0`};
		position: relative;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin: ${({ theme }) => theme.spacings.medium};
		}
	`;
}
